import React, { useEffect, useMemo, useState } from 'react'
import Header from './Header';
import { getKnownLangaugesForStudent, getLotsForTTS, getRejectedSentencesForReviewer, pullMoreLotsForTTS, releaseLotForUserTTS, revertLotForUserTTS, submitLotForUser, submitLotForUserTTS } from '@/store/thunks/studentTTT';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { setEditSentencesTTS, setSelectedLotId, setSelectedTargetId, setSelectedTargetName, setShowMarkedSentencesTTS, setStudentIdForTTS } from '@/store/slices/studentDashboard';
import GenericTableStudent from '../GenericTableStudent';

const LotsForTTS = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [knownLanguages, setKnownLanguages] = useState(null);
    const [selectedLanguage, setSelectedLanguage] = useState({
        languageId: "",
        languageName: "",
    });
    const { role } = useSelector((state) => state.studentAuth);
    const [targetName, setTargetName] = useState("");
    const [files, setFiles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedFile, setSelectedFile] = useState(null);
    const [dataVersion, setDataVersion] = useState(0);
    const [tableData, setTableData] = useState([]);

    const [pageIndex, setPageIndex] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    const [activeTab, setActiveTab] = useState("active"); // active | rejected
    const [subTab, setSubTab] = useState("assigned");

    const [selectedRow, setSelectedRow] = useState(null);


    const paginatedData = useMemo(() => {
        const start = pageIndex * pageSize;
        const end = start + pageSize;
        return files.slice(start, end);
    }, [files, pageIndex, pageSize]);

    const hasNextPage = (pageIndex + 1) * pageSize < files.length;


    // Fetch data from API
    useEffect(() => {
        if (activeTab === "active") {
            getLotsForUser();
            getKnownLanguages();
        } else if (activeTab === "rejected") {
            getRejectedSentencesForUser();
        }
    }, [dispatch, activeTab, subTab]);



    // done
    const getLotsForUser = async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await dispatch(getLotsForTTS(subTab));

            if (res.data && Array.isArray(res.data) && res.data.length > 0) {
                setFiles(res.data);
            } else {
                setFiles([]);
                setSelectedFile([]);
                // Each lot expected to have some text data for now
                setTableData([]);
            }
        } catch (err) {
            console.error("❌ Error fetching lots:", err);
            setError(err.message || "Failed to fetch lots.");
            toast.error("Failed to load lots. Please try again later.");
        } finally {
            setLoading(false);
        }
    };



    const getRejectedSentencesForUser = async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await dispatch(getRejectedSentencesForReviewer('tts'));
            if (res.data && Array.isArray(res.data)) {
                const freshData = res.data;
                setFiles(freshData);
                setTableData(freshData.map(item => ({
                    ...item,
                    targetText: item.translatedText || ""
                })));
                setDataVersion(v => v + 1); // ← Force update
            } else {
                setFiles([]);
                setTableData([]);
            }
        } catch (err) {
            console.error("❌ Error fetching sentences:", err);
            toast.error("Failed to load sentences.");
        } finally {
            setLoading(false);
        }
    };


    // done
    const getKnownLanguages = async () => {
        try {
            setLoading(true);
            setError(null);
            const res = await dispatch(getKnownLangaugesForStudent());
            if (res && Array.isArray(res.data) && res.data.length > 0) {
                setKnownLanguages(res.data);
            } else {
                setKnownLanguages([]);
            }
        }
        catch (error) {
            console.log("Error fetching known languages:", error);
        }
    }

    const handleLanguageChange = (e) => {
        const selectedId = e.target.value;
        const selectedName =
            e.target.options[e.target.selectedIndex].getAttribute("data-name");

        setSelectedLanguage({
            languageId: selectedId,
            languageName: selectedName,
        });
    };


    // done
    const releaseLot = async () => {

        // 🟠 Styled confirmation toast (matching your app’s theme)
        toast.info(
            ({ closeToast }) => (
                <div className="flex flex-col items-center text-center px-4 py-2">
                    <p className="mb-4 text-gray-800 text-sm sm:text-base font-medium">
                        Do you want to release Extra Lot?
                    </p>
                    <div className="flex gap-3 justify-center">
                        <button
                            onClick={async () => {
                                closeToast();
                                await handleRelease();
                            }}
                            className="px-4 py-1.5 bg-[#ec7026] hover:bg-[#d15c14] text-white rounded-md font-medium text-sm shadow-sm transition-all duration-200"
                        >
                            ✅ Yes, release
                        </button>
                        <button
                            onClick={() => {
                                toast.info("❌ Pull operation cancelled.", {
                                    position: "top-center",
                                    autoClose: 2000,
                                });
                                closeToast();
                            }}
                            className="px-4 py-1.5 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-md font-medium text-sm shadow-sm transition-all duration-200"
                        >
                            Cancel
                        </button>
                    </div>
                </div>
            ),
            {
                toastId: "release-lot-confirmation",
                autoClose: false,
                closeOnClick: false,
                draggable: false,
                position: "top-center",
                hideProgressBar: true,
            }
        );

        // 🧩 Helper function for actual pull logic
        const handleRelease = async () => {
            setLoading(true);
            setError(null);
            try {
                console.log(selectedRow)
                const res = await dispatch(releaseLotForUserTTS(selectedRow.original.lotId, selectedRow.original.sourceId));
                if (res.success) {
                    toast.success("🎉 Lot released!", {
                        position: "top-center",
                        autoClose: 2500,
                    });
                    await getLotsForUser();
                } else {
                    toast.error(res.message, {
                        position: "top-center",
                        autoClose: 2500,
                    });
                    await getLotsForUser();
                }
            } catch (err) {
                console.error("❌ Error pulling lots:", err);
                setError(err.message || "Failed to pull lots.");
                toast.error("⚠️ Failed to pull more lots. Please try again later.", {
                    position: "top-center",
                });
            } finally {
                setLoading(false);
            }
        };
    };

    // done
    const pullLots = async (selectedLanguage) => {
        if (!selectedLanguage?.languageId) {
            toast.warn("⚠️ Please select a language before pulling more lots.", {
                position: "top-center",
                toastId: "pull-lot-confirmation-lang",
            });
            return;
        }

        // 🟠 Styled confirmation toast (matching your app’s theme)
        toast.info(
            ({ closeToast }) => (
                <div className="flex flex-col items-center text-center px-4 py-2">
                    <p className="mb-4 text-gray-800 text-sm sm:text-base font-medium">
                        Do you want to pull more lots for{" "}
                        <span className="font-semibold text-[#ec7026]">
                            {selectedLanguage.languageName}
                        </span>
                        ?
                    </p>
                    <div className="flex gap-3 justify-center">
                        <button
                            onClick={async () => {
                                closeToast();
                                await handlePull(selectedLanguage.languageId);
                            }}
                            className="px-4 py-1.5 bg-[#ec7026] hover:bg-[#d15c14] text-white rounded-md font-medium text-sm shadow-sm transition-all duration-200"
                        >
                            ✅ Yes, Pull
                        </button>
                        <button
                            onClick={() => {
                                toast.info("❌ Pull operation cancelled.", {
                                    position: "top-center",
                                    autoClose: 2000,
                                });
                                closeToast();
                            }}
                            className="px-4 py-1.5 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-md font-medium text-sm shadow-sm transition-all duration-200"
                        >
                            Cancel
                        </button>
                    </div>
                </div>
            ),
            {
                toastId: "pull-lot-confirmation",
                autoClose: false,
                closeOnClick: false,
                draggable: false,
                position: "top-center",
                hideProgressBar: true,
            }
        );

        // 🧩 Helper function for actual pull logic
        const handlePull = async (lang) => {
            setLoading(true);
            setError(null);
            try {
                const res = await dispatch(pullMoreLotsForTTS(lang));

                if (res.success) {
                    toast.success("🎉 New lot pulled successfully!", {
                        position: "top-center",
                        autoClose: 2500,
                    });
                    await getLotsForUser();
                } else {
                    toast.error(res.message)
                }
            } catch (err) {
                console.error("❌ Error pulling lots:", err);
                setError(err.message || "Failed to pull lots.");
                toast.error("⚠️ Failed to pull more lots. Please try again later.", {
                    position: "top-center",
                });
            } finally {
                setLoading(false);
            }
        };
    };

    const handleTargetChange = React.useCallback((index, value) => {
        setTableData(prev => {
            const updated = [...prev];
            updated[index] = { ...updated[index], targetText: value };
            return updated;
        });
    }, []);

    const goToLot = async (row) => {
        await dispatch(setSelectedLotId(row.original.lotId));
        if (role === 'REVIEWER') await dispatch(setStudentIdForTTS(row.original.studentId));
        await dispatch(setSelectedTargetId(row.original.sourceId));
        await dispatch(setSelectedTargetName(row.original.sourceLangName));
        const showMarkedSentence = (row.original.studentStatus === 'COMPLETED' && row.original.reviewerStatus === 'COMPLETED') || (row.original.studentStatus === 'RESUBMITTED' && row.original.reviewerStatus === 'UNDER_REVIEW') || (row.original.studentStatus === 'IN_PROGRESS' && row.original.reviewerStatus === 'CHANGES_REQUIRED') || (row.original.studentStatus === 'SUBMITTED' && row.original.reviewerStatus === 'UNDER_REVIEW');
        await dispatch(setShowMarkedSentencesTTS(showMarkedSentence))
        const editSentences = row.original.studentStatus === 'IN_PROGRESS' || row.original.studentStatus === 'ASSIGNED';
        await dispatch(setEditSentencesTTS(editSentences));
        navigate(`/${role === 'REVIEWER' ? 'reviewer' : 'student'}/lots-for-TTS/ASR_TTS/${row.original.lotId}`);
    }

    const submitLot = async (row) => {
        const lotId = row.original.lotId;
        const targetName = row.original.targetLangName || "this lot";

        // Confirmation Toast
        toast.info(
            ({ closeToast }) => (
                <div className="flex flex-col gap-4 p-2">
                    <p className="text-gray-800 font-medium">
                        Are you sure you want to <span className="text-amber-600 font-bold">submit</span> this lot?
                    </p>
                    <p className="text-sm text-gray-600">
                        Lot ID: <span className="font-mono font-bold">{lotId}</span>
                        <br />
                        Target: <span className="font-medium">{targetName}</span>
                    </p>
                    <p className="text-sm text-red-600 font-medium">
                        This action cannot be undone!
                    </p>

                    <div className="flex gap-3 justify-center mt-4">
                        {/* Confirm Button */}
                        <button
                            onClick={async () => {
                                closeToast();
                                await handleSubmit(); // Proceed with submission
                            }}
                            className="px-5 py-2 bg-amber-600 hover:bg-amber-700 text-white rounded-lg font-medium shadow-md transition-all"
                        >
                            Yes, Submit
                        </button>

                        {/* Cancel Button */}
                        <button
                            onClick={() => {
                                closeToast();
                                toast.info("Submission cancelled.", {
                                    autoClose: 2000,
                                    position: "top-center",
                                });
                            }}
                            className="px-5 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg font-medium transition-all"
                        >
                            Cancel
                        </button>
                    </div>
                </div>
            ),
            {
                toastId: "submit-lot-confirmation",
                position: "top-center",
                autoClose: false,
                closeOnClick: false,
                draggable: false,
                closeButton: false,
                className: "border-2 border-amber-400 rounded-xl shadow-2xl",
            }
        );

        // Actual submission logic (only runs after user clicks "Yes")
        const handleSubmit = async () => {
            setLoading(true);
            setError(null);

            try {
                const res = await dispatch(submitLotForUserTTS(lotId, row.original.sourceId, row.original.studentId));

                if (res.success) {
                    toast.success("Lot submitted successfully!", {
                        position: "top-right",
                        autoClose: 3000,
                    });
                    // Optional: refresh data or redirect
                    await getLotsForUser();
                } else {
                    toast.error(res.message, {
                        position: "top-right",
                    });
                }
            } catch (err) {
                console.log(err)
                toast.error(err.message, {
                    position: "top-center",
                });
            } finally {
                setLoading(false);
            }
        };
    };

    const revertLot = async (row) => {
        const lotId = row.original.lotId;
        const targetName = row.original.targetLangName;

        // Confirmation Toast
        toast.info(
            ({ closeToast }) => (
                <div className="flex flex-col gap-4 p-2">
                    <p className="text-gray-800 font-medium">
                        Are you sure you want to <span className="text-amber-600 font-bold">revert</span> this lot?
                    </p>
                    <p className="text-sm text-gray-600">
                        Lot ID: <span className="font-mono font-bold">{lotId}</span>
                        <br />
                        Target: <span className="font-medium">{targetName}</span>
                    </p>
                    <p className="text-sm text-red-600 font-medium">
                        This action cannot be undone!
                    </p>

                    <div className="flex gap-3 justify-center mt-4">
                        {/* Confirm Button */}
                        <button
                            onClick={async () => {
                                closeToast();
                                await handleRevert(); // Proceed with submission
                            }}
                            className="px-5 py-2 bg-amber-600 hover:bg-amber-700 text-white rounded-lg font-medium shadow-md transition-all"
                        >
                            Yes, Revert
                        </button>

                        {/* Cancel Button */}
                        <button
                            onClick={() => {
                                closeToast();
                                toast.info("Submission cancelled.", {
                                    autoClose: 2000,
                                    position: "top-center",
                                });
                            }}
                            className="px-5 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg font-medium transition-all"
                        >
                            Cancel
                        </button>
                    </div>
                </div>
            ),
            {
                toastId: "revert-lot-confirmation",
                position: "top-center",
                autoClose: false,
                closeOnClick: false,
                draggable: false,
                closeButton: false,
                className: "border-2 border-amber-400 rounded-xl shadow-2xl",
            }
        );

        // Actual submission logic (only runs after user clicks "Yes")
        const handleRevert = async () => {
            setLoading(true);
            setError(null);

            try {
                console.log(row)
                const res = await dispatch(revertLotForUserTTS(lotId, row.original.sourceId, row.original.studentId));

                if (res.success) {
                    toast.success("Lot reverted successfully!", {
                        position: "top-right",
                        autoClose: 3000,
                    });
                    // Optional: refresh data or redirect
                } else {
                    toast.error(res.message, {
                        position: "top-right",
                    });
                }
            } catch (err) {
                console.log(err)
                toast.error(err.message, {
                    position: "top-center",
                });
            } finally {
                setLoading(false);
            }
        };
    };

    const fileColumns = React.useMemo(() => {
        const columns = [
            {
                header: "Sr. No.",
                cell: ({ row }) => pageIndex * pageSize + row.index + 1,
                meta: { width: "10%" },
                cellClassName: "text-center"
            },
            {
                header: "Lot Name",
                cell: ({ row }) => (
                    <span className="text-gray-800 font-medium block whitespace-pre-wrap underline hover:cursor-pointer text-orange-600" onClick={() => goToLot(row)}>
                        {row.original.lotName}
                    </span>
                ),
            },
            {
                header: "Total Sentences",
                cell: ({ row }) => (
                    <span className="text-gray-800 font-medium block whitespace-pre-wrap">
                        {row.original.totalSentences}
                    </span>
                ),
                // meta: { width: "35%" },
            },
            {
                header: "Source Language",
                cell: ({ row }) => (
                    <span className="text-gray-800 font-medium block whitespace-pre-wrap">
                        {row.original.sourceLangName || row.original.targetLangName}
                    </span>
                ),
                // meta: { width: "35%" },
            },
            {
                header: "Student Status",
                accessorKey: "studentStatus",
                cell: ({ row }) => (
                    <span
                        className={`font-semibold ${row.original.studentStatus === "PENDING"
                            ? "text-yellow-600"
                            : row.original.studentStatus === "VALIDATED"
                                ? "text-green-600"
                                : "text-gray-800"
                            }`}
                    >
                        {row.original.studentStatus || "PENDING"}
                    </span>
                ),
                // meta: { width: "10%" },
            },
            {
                header: "Reviewer Status",
                accessorKey: "reviewerStatus",
                cell: ({ row }) => (
                    <span
                        className={`font-semibold ${row.original.reviewerStatus === "PENDING"
                            ? "text-yellow-600"
                            : row.original.reviewerStatus === "VALIDATED"
                                ? "text-green-600"
                                : "text-gray-800"
                            }`}
                    >
                        {row.original.reviewerStatus || "PENDING"}
                    </span>
                ),
                // meta: { width: "10%" },
            },
            {
                header: "Completion Status",
                cell: ({ row }) => {
                    const validated = row.original.validatedRecordingsCount || row.original.validatedSentencesCount || 0;
                    const reviewed = row.original.reviewedRecordingsCount || row.original.reviewedSentencesCount || 0;
                    const rejected = row.original.rejectedRecordingsCount || row.original.rejectedSentencesCount || 0;
                    const percent = row.original.completedPercentage || 0;

                    return (
                        <div className="w-full space-y-2">
                            <div className="text-md text-gray-600 flex justify-between ">
                                <span><strong>Validated:</strong> {validated}</span> | <span><strong>Reviewed:</strong> {reviewed}</span> | <span><strong>Rejected:</strong> {rejected}</span>
                            </div>

                            {/* Progress Bar */}
                            <div className="w-full bg-gray-200 rounded-full h-3">
                                <div
                                    className="bg-green-500 h-3 rounded-full transition-all duration-500"
                                    style={{ width: `${percent}%` }}
                                />
                            </div>

                            <div className="text-md font-semibold text-green-700 text-right">
                                {percent}% Completed
                            </div>
                        </div>
                    );
                },
            },
            {
                header: "Assigned At",
                accessorKey: "assignedAt",
                cellClassName: "cursor-pointer hover:text-blue-600 text-center",
                cell: ({ row }) => {
                    const deadline = row.original?.assignedAt;
                    if (!deadline) return "N/A";
                    try {
                        const date = new Date(deadline);
                        const now = new Date();
                        const isOverdue = date < now;
                        const isApproaching = !isOverdue && date - now < 7 * 24 * 60 * 60 * 1000;
                        return (
                            <div>
                                <div
                                    className={
                                        isOverdue
                                            ? "text-red-600"
                                            : isApproaching
                                                ? "text-amber-600"
                                                : null
                                    }
                                >
                                    {date.toLocaleDateString("en-US", {
                                        year: "numeric",
                                        month: "long",
                                        day: "numeric",
                                    })}
                                </div>
                                <div className="text-sm text-gray-500">
                                    {date.toLocaleTimeString("en-US", {
                                        hour: "numeric",
                                        minute: "numeric",
                                        hour12: true,
                                    })}
                                </div>
                            </div>
                        );
                    } catch (e) {
                        return deadline;
                    }
                },
            },
            {
                header: "Deadline",
                accessorKey: "deadline",
                cell: ({ row }) => {
                    const deadline = row.original?.deadline;
                    if (!deadline) return "N/A";
                    try {
                        const date = new Date(deadline);
                        const now = new Date();
                        const isOverdue = date < now;
                        const isApproaching = !isOverdue && date - now < 7 * 24 * 60 * 60 * 1000;
                        return (
                            <div>
                                <div
                                    className={
                                        isOverdue
                                            ? "text-red-600"
                                            : isApproaching
                                                ? "text-amber-600"
                                                : null
                                    }
                                >
                                    {date.toLocaleDateString("en-US", {
                                        year: "numeric",
                                        month: "long",
                                        day: "numeric",
                                    })}
                                </div>
                                <div className="text-sm text-gray-500">
                                    {date.toLocaleTimeString("en-US", {
                                        hour: "numeric",
                                        minute: "numeric",
                                        hour12: true,
                                    })}
                                </div>
                            </div>
                        );
                    } catch (e) {
                        return deadline;
                    }
                },
                // meta: { width: "35%" },
            },
            subTab === "assigned" && {
                header: "Buttons",
                cell: ({ row }) => {
                    return (
                        <div className='flex flex-col gap-2'>
                            {(row.original.studentStatus === 'ASSIGNED' || row.original.studentStatus === 'IN_PROGRESS' || role === 'REVIEWER') && <button
                                className="px-4 py-2 text-sm font-semibold text-white bg-blue-600 
                   hover:bg-blue-700 rounded-md shadow-sm min-w-[90px]"
                                onClick={() => submitLot(row)}
                            >
                                Submit
                            </button>}
                            {role === 'REVIEWER' && (
                                <button
                                    className="px-4 py-2 text-sm font-semibold text-white bg-orange-600 
        hover:bg-orange-700 rounded-md shadow-sm min-w-[90px]"
                                    onClick={() => revertLot(row)}
                                >
                                    Revert
                                </button>
                            )}
                        </div>
                    );
                },
            }
        ]

        return columns.filter(Boolean);

    }, [pageIndex, pageSize, targetName, handleTargetChange, subTab, role]); // ← tableData REMOVED from deps!


    return (
        <div className="bg-[#fffaf7] min-h-screen">
            <Header />

            <div className="flex p-2">

                {/* Main Content */}
                <main
                    className={`flex-1 pl-4 pr-4 bg-[#fffaf7] transition-all duration-300 `}
                >
                    <div className="bg-white shadow-lg rounded-2xl p-4 m-2 w-full">
                        {/* Top Bar */}
                        <div className="flex items-center justify-between mb-5">
                            <div className="flex items-center gap-3">
                                <button
                                    className="text-gray-700 hover:text-gray-900 focus:outline-none"
                                >
                                </button>
                                <h2 className="text-xl font-semibold text-gray-800">
                                    Lots for Text-to-Speech Translation
                                </h2>
                            </div>

                            {role !== 'REVIEWER' &&
                                <div className="flex gap-3 items-center">
                                    <button
                                        onClick={() => pullLots(selectedLanguage)}
                                        className={`px-4 py-2 bg-green-600 text-white rounded-lg font-medium text-sm shadow-sm 
                    hover:bg-green-700 focus:ring-2 focus:ring-green-400 transition-all duration-200`}
                                    >
                                        Pull Lot
                                    </button>

                                    <button
                                        onClick={() => releaseLot()}
                                        className={`px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium shadow-sm transition-all duration-200
      ${selectedRow ? "" : "opacity-60 cursor-not-allowed"}`}
                                        disabled={!selectedRow}
                                    >
                                        Release Lot
                                    </button>


                                    <select
                                        className="px-4 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-700 font-medium text-sm
    hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-400 cursor-pointer transition-all duration-200"
                                        defaultValue=""
                                        value={selectedLanguage.languageId}
                                        onChange={handleLanguageChange}
                                    >
                                        <option value="" disabled>
                                            Select a language
                                        </option>
                                        {knownLanguages && knownLanguages.length > 0 &&
                                            knownLanguages.map((lang) => (
                                                <option
                                                    key={lang.languageId}
                                                    value={lang.languageId}
                                                    data-name={lang.languageName} // store language name in custom attribute
                                                >
                                                    {lang.languageName}
                                                </option>
                                            ))
                                        }
                                    </select>

                                </div>
                            }
                        </div>

                        {role === "REVIEWER" && (
                            <div className="flex gap-4 mb-6">
                                <button
                                    onClick={() => setActiveTab("active")}
                                    className={`px-4 py-2 rounded-lg font-medium transition ${activeTab === "active"
                                        ? "bg-amber-600 text-white"
                                        : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                                        }`}
                                >
                                    Lots
                                </button>

                                <button
                                    onClick={() => setActiveTab("rejected")}
                                    className={`px-4 py-2 rounded-lg font-medium transition ${activeTab === "rejected"
                                        ? "bg-red-600 text-white"
                                        : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                                        }`}
                                >
                                    Sentences
                                </button>
                            </div>
                        )}

                        {activeTab === "active" && (
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">

                                {/* Assigned Card */}
                                <div
                                    className={`cursor-pointer p-4 rounded-xl shadow-md border transition-all ${subTab === "assigned"
                                        ? "border-amber-500 bg-amber-50"
                                        : "border-gray-200 hover:bg-gray-50"
                                        }`}
                                    onClick={() => setSubTab("assigned")}
                                >
                                    <div className="flex items-center gap-3">
                                        <span className="text-xl">📂</span>
                                        <div>
                                            <h3 className="text-md font-semibold text-gray-800">
                                                Assigned Lots
                                            </h3>
                                            <p className="text-sm text-gray-500">Currently under review</p>
                                        </div>
                                    </div>
                                </div>

                                {/* Completed Card */}
                                <div
                                    className={`cursor-pointer p-4 rounded-xl shadow-md border transition-all ${subTab === "completed"
                                        ? "border-amber-500 bg-amber-50"
                                        : "border-gray-200 hover:bg-gray-50"
                                        }`}
                                    onClick={() => setSubTab("completed")}
                                >
                                    <div className="flex items-center gap-3">
                                        <span className="text-xl">✔️</span>
                                        <div>
                                            <h3 className="text-md font-semibold text-gray-800">
                                                Completed Lots
                                            </h3>
                                            <p className="text-sm text-gray-500">Reviewed & finalized</p>
                                        </div>
                                    </div>
                                </div>

                            </div>
                        )}

                        {/* Data Table */}
                        {files && files.length > 0 ? (
                            <div className="border border-gray-200 rounded-xl">
                                <GenericTableStudent
                                    data={paginatedData}
                                    columns={fileColumns}
                                    showPagination={true}
                                    manualPagination={true}
                                    pageIndex={pageIndex}
                                    pageSize={pageSize}
                                    hasNextPage={hasNextPage}
                                    onPaginationChange={({ pageIndex, pageSize }) => {
                                        setPageIndex(pageIndex);
                                        setPageSize(pageSize);
                                    }}
                                    options={{
                                        selectedRow,
                                        onRowSelect: (row) => setSelectedRow(row),
                                    }}
                                />
                            </div>
                        ) : (
                            <div className="text-center text-gray-500 py-10 text-lg">
                                No {activeTab === 'active' ? 'Lots' : 'sentences'} available for this {role === 'REVIEWER' ? 'reviewer' : 'user'}.
                            </div>
                        )}
                    </div>
                </main>
            </div>
        </div>
    );
}

export default LotsForTTS
